import React from 'react';
import styled, { keyframes } from 'styled-components';
import { QA_STATES } from './qaStateMachine';

const pulse = keyframes`
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.7; }
    100% { transform: scale(1); opacity: 1; }
`;

const wave = keyframes`
    0%, 100% { height: 8px; }
    50% { height: 20px; }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 24px;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-radius: 12px;
    color: white;
    min-width: 200px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ProgressHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 12px;
`;

const QuestionNumber = styled.div`
    font-size: 1.5rem;
    font-weight: bold;
    color: #00d9ff;
`;

const TotalQuestions = styled.span`
    font-size: 1rem;
    color: #888;
    margin-left: 4px;
`;

const ProgressBar = styled.div`
    width: 100%;
    height: 6px;
    background: #333;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 16px;
`;

const ProgressFill = styled.div`
    height: 100%;
    background: linear-gradient(90deg, #00d9ff, #00ff88);
    border-radius: 3px;
    transition: width 0.3s ease;
    width: ${props => props.$progress}%;
`;

const StatusContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: ${props => props.$bgColor || 'rgba(255, 255, 255, 0.05)'};
    border-radius: 8px;
    width: 100%;
`;

const StatusIcon = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${props => props.$bgColor || '#333'};
    animation: ${props => props.$animate ? pulse : 'none'} 1.5s ease-in-out infinite;
`;

const StatusText = styled.div`
    flex: 1;
`;

const StatusTitle = styled.div`
    font-size: 0.9rem;
    font-weight: 600;
    color: ${props => props.$color || '#fff'};
    margin-bottom: 2px;
`;

const StatusSubtitle = styled.div`
    font-size: 0.75rem;
    color: #888;
`;

const AudioWaves = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    height: 24px;
`;

const AudioBar = styled.div`
    width: 4px;
    background: #00ff88;
    border-radius: 2px;
    animation: ${wave} 0.8s ease-in-out infinite;
    animation-delay: ${props => props.$delay}s;
`;

const CountdownDisplay = styled.div`
    font-size: 3rem;
    font-weight: bold;
    color: #00d9ff;
    text-align: center;
    animation: ${pulse} 1s ease-in-out infinite;
`;

const SkippedBadge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    background: #ff6b6b;
    color: white;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
    margin-left: 8px;
`;

/**
 * QAProgressIndicator - Shows progress and current state of Q&A session
 *
 * @param {Object} props
 * @param {string} props.state - Current QA_STATE
 * @param {number} props.currentQuestion - Current question number (1-indexed)
 * @param {number} props.totalQuestions - Total number of questions
 * @param {number} props.skippedCount - Number of skipped questions
 * @param {number} props.countdown - Countdown value (optional, for COUNTDOWN state)
 * @param {string} props.participantName - Name of Murphy participant asking
 */
const QAProgressIndicator = ({
    state,
    currentQuestion,
    totalQuestions,
    skippedCount = 0,
    countdown = 3,
    participantName = 'Murphy'
}) => {
    const progress = state === QA_STATES.COMPLETE
        ? 100
        : (totalQuestions > 0 ? ((currentQuestion - 1) / totalQuestions) * 100 : 0);

    const renderStatus = () => {
        switch (state) {
            case QA_STATES.IDLE:
                return (
                    <StatusContainer>
                        <StatusIcon $bgColor="#333">
                            <span role="img" aria-label="ready">🎯</span>
                        </StatusIcon>
                        <StatusText>
                            <StatusTitle>Ready to Start</StatusTitle>
                            <StatusSubtitle>Click start to begin the Q&A session</StatusSubtitle>
                        </StatusText>
                    </StatusContainer>
                );

            case QA_STATES.COUNTDOWN:
                return (
                    <StatusContainer $bgColor="rgba(0, 217, 255, 0.1)">
                        <CountdownDisplay>{countdown}</CountdownDisplay>
                        <StatusText>
                            <StatusTitle $color="#00d9ff">Get Ready!</StatusTitle>
                            <StatusSubtitle>First question coming up...</StatusSubtitle>
                        </StatusText>
                    </StatusContainer>
                );

            case QA_STATES.MURPHY_ASKING:
                return (
                    <StatusContainer $bgColor="rgba(255, 193, 7, 0.1)">
                        <StatusIcon $bgColor="#ffc107" $animate>
                            <span role="img" aria-label="speaking">🎤</span>
                        </StatusIcon>
                        <StatusText>
                            <StatusTitle $color="#ffc107">{participantName} is asking...</StatusTitle>
                            <StatusSubtitle>Listen to the question</StatusSubtitle>
                        </StatusText>
                    </StatusContainer>
                );

            case QA_STATES.AWAITING_ANSWER:
                return (
                    <StatusContainer $bgColor="rgba(0, 255, 136, 0.1)">
                        <AudioWaves>
                            <AudioBar $delay={0} />
                            <AudioBar $delay={0.15} />
                            <AudioBar $delay={0.3} />
                            <AudioBar $delay={0.45} />
                            <AudioBar $delay={0.6} />
                        </AudioWaves>
                        <StatusText>
                            <StatusTitle $color="#00ff88">Listening...</StatusTitle>
                            <StatusSubtitle>Speak your answer clearly</StatusSubtitle>
                        </StatusText>
                    </StatusContainer>
                );

            case QA_STATES.PROCESSING:
                return (
                    <StatusContainer $bgColor="rgba(138, 43, 226, 0.1)">
                        <StatusIcon $bgColor="#8a2be2" $animate>
                            <span role="img" aria-label="processing">⏳</span>
                        </StatusIcon>
                        <StatusText>
                            <StatusTitle $color="#8a2be2">Processing...</StatusTitle>
                            <StatusSubtitle>Moving to next question</StatusSubtitle>
                        </StatusText>
                    </StatusContainer>
                );

            case QA_STATES.COMPLETE:
                return (
                    <StatusContainer $bgColor="rgba(0, 255, 136, 0.15)">
                        <StatusIcon $bgColor="#00ff88">
                            <span role="img" aria-label="complete">✅</span>
                        </StatusIcon>
                        <StatusText>
                            <StatusTitle $color="#00ff88">Session Complete!</StatusTitle>
                            <StatusSubtitle>
                                {totalQuestions - skippedCount} of {totalQuestions} answered
                                {skippedCount > 0 && <SkippedBadge>{skippedCount} skipped</SkippedBadge>}
                            </StatusSubtitle>
                        </StatusText>
                    </StatusContainer>
                );

            case QA_STATES.ERROR:
                return (
                    <StatusContainer $bgColor="rgba(255, 107, 107, 0.1)">
                        <StatusIcon $bgColor="#ff6b6b">
                            <span role="img" aria-label="error">⚠️</span>
                        </StatusIcon>
                        <StatusText>
                            <StatusTitle $color="#ff6b6b">Error</StatusTitle>
                            <StatusSubtitle>Something went wrong</StatusSubtitle>
                        </StatusText>
                    </StatusContainer>
                );

            default:
                return null;
        }
    };

    return (
        <Container>
            <ProgressHeader>
                <QuestionNumber>
                    Q{currentQuestion}
                    <TotalQuestions>/ {totalQuestions}</TotalQuestions>
                </QuestionNumber>
                {skippedCount > 0 && (
                    <SkippedBadge>{skippedCount} skipped</SkippedBadge>
                )}
            </ProgressHeader>

            <ProgressBar>
                <ProgressFill $progress={progress} />
            </ProgressBar>

            {renderStatus()}
        </Container>
    );
};

export default QAProgressIndicator;
